import React, { useState, useMemo } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import IconProvider from '@/components/ui/IconProvider';
import { OverflowIssue, OverflowReport } from '@/lib/utils/overflowDetection';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface OverflowReportPanelProps {
  report: OverflowReport | null;
  isChecking: boolean;
  onCheck: () => void;
  currentLanguage: string;
  onLanguageSelect?: (languageCode: string) => void;
}

export default function OverflowReportPanel({
  report,
  isChecking,
  onCheck,
  currentLanguage,
  onLanguageSelect
}: OverflowReportPanelProps) {
  const { t } = usePlatformTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Group issues by language
  const issuesByLanguage = useMemo(() => {
    if (!report) return new Map<string, OverflowIssue[]>();
    
    const map = new Map<string, OverflowIssue[]>();
    report.issues.forEach(issue => {
      const existing = map.get(issue.language) || [];
      existing.push(issue);
      map.set(issue.language, existing);
    });
    
    return map;
  }, [report]);

  // Group issues by element
  const issuesByElement = useMemo(() => {
    if (!report) return new Map<string, OverflowIssue[]>();
    
    const map = new Map<string, OverflowIssue[]>();
    report.issues.forEach(issue => {
      const existing = map.get(issue.elementSelector) || [];
      existing.push(issue);
      map.set(issue.elementSelector, existing);
    });
    
    return map;
  }, [report]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getOverflowDescription = (issue: OverflowIssue) => {
    const { overflowType, overflowAmount } = issue;
    
    if (overflowType === 'both') {
      return `Overflows by ${Math.round(overflowAmount.width)}px (width) and ${Math.round(overflowAmount.height)}px (height)`;
    } else if (overflowType === 'width') {
      return `Overflows by ${Math.round(overflowAmount.width)}px (width)`;
    } else {
      return `Overflows by ${Math.round(overflowAmount.height)}px (height)`;
    }
  };

  const getSeverityColor = (languageCount: number) => {
    if (languageCount > 3) return 'text-red-600 bg-red-50';
    if (languageCount > 1) return 'text-orange-600 bg-orange-50';
    return 'text-amber-600 bg-amber-50';
  };

  const getSeverityBadge = (languageCount: number) => {
    if (languageCount > 3) return 'Critical';
    if (languageCount > 1) return 'High';
    return 'Warning';
  };

  return (
    <div className="border border-input rounded-lg bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-input bg-muted/50">
        <div className="flex items-center gap-2">
          <IconProvider iconstring="fa-search" size={14} />
          <span className="text-sm font-medium">{t('platform.ui.overflowreportpanel.overflow_detection')}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCheck}
            disabled={isChecking}
            className="h-7 text-xs gap-1"
          >
            {isChecking ? (
              <>
                <IconProvider iconstring="fa-spinner" size={12} className="animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <IconProvider iconstring="fa-magnifying-glass" size={12} />
                Check All Languages
              </>
            )}
          </Button>
          {report && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-7 w-7 p-0"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && report && (
        <div className="max-h-[400px] overflow-y-auto">
          {/* Summary */}
          <div className="p-3 border-b border-input bg-muted/20">
            {report.issues.length === 0 ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle size={16} />
                <span className="text-sm">{t('platform.ui.overflowreportpanel.no_overflow_issues_detected')}</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-amber-600">
                  <AlertTriangle size={16} className="mt-0.5" />
                  <div className="text-sm">
                    <div className="font-medium">
                      Found {report.issues.length} overflow issue{report.issues.length !== 1 ? 's' : ''}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {report.elementsWithIssues} element{report.elementsWithIssues !== 1 ? 's' : ''} affected across{' '}
                      {report.languagesWithIssues.size} language{report.languagesWithIssues.size !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {report.issues.length > 0 && (
            <div className="divide-y divide-input">
              {/* Group by Language */}
              <div className="p-3">
                <button
                  onClick={() => toggleSection('by-language')}
                  className="flex items-center gap-2 text-sm font-medium mb-2 hover:text-primary"
                >
                  {expandedSections.has('by-language') ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Issues by Language
                </button>
                
                {expandedSections.has('by-language') && (
                  <div className="ml-6 space-y-2">
                    {Array.from(issuesByLanguage.entries()).map(([languageCode, issues]) => {
                      const languageName = issues[0]?.languageName || languageCode;
                      const isCurrentLanguage = languageCode === currentLanguage;
                      
                      return (
                        <div
                          key={languageCode}
                          className={`p-2 rounded border ${
                            isCurrentLanguage ? 'border-primary bg-primary/5' : 'border-input bg-muted/20'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{languageName}</span>
                              {isCurrentLanguage && (
                                <span className="text-xs px-1.5 py-0.5 bg-primary text-primary-foreground rounded">
                                  Current
                                </span>
                              )}
                            </div>
                            <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-medium">
                              {issues.length} issue{issues.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-1">
                            {issues.slice(0, 3).map((issue, idx) => (
                              <div key={idx}>
                                <div className="font-medium text-foreground">
                                  LocaKey: {issue.locaKey}
                                </div>
                                <div className="text-xs">
                                  {getOverflowDescription(issue)}
                                </div>
                              </div>
                            ))}
                            {issues.length > 3 && (
                              <div className="text-xs text-muted-foreground italic">
                                +{issues.length - 3} more...
                              </div>
                            )}
                          </div>
                          {onLanguageSelect && !isCurrentLanguage && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onLanguageSelect(languageCode)}
                              className="h-6 text-xs mt-2"
                            >
                              Switch to this language
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Group by Element */}
              <div className="p-3">
                <button
                  onClick={() => toggleSection('by-element')}
                  className="flex items-center gap-2 text-sm font-medium mb-2 hover:text-primary"
                >
                  {expandedSections.has('by-element') ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Issues by Element
                </button>
                
                {expandedSections.has('by-element') && (
                  <div className="ml-6 space-y-2">
                    {Array.from(issuesByElement.entries()).map(([selector, issues]) => {
                      const affectedLanguages = new Set(issues.map(i => i.languageName));
                      const languageCount = affectedLanguages.size;
                      
                      return (
                        <div
                          key={selector}
                          className="p-2 rounded border border-input bg-muted/20"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded flex-1 break-all">
                              {selector}
                            </code>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${getSeverityColor(languageCount)}`}>
                              {getSeverityBadge(languageCount)}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <div className="mb-1">
                              Affected languages: {Array.from(affectedLanguages).join(', ')}
                            </div>
                            <div className="space-y-1 mt-2">
                              {issues.slice(0, 2).map((issue, idx) => (
                                <div key={idx} className="pl-2 border-l-2 border-muted">
                                  <div className="font-medium text-foreground">
                                    {issue.languageName}: {issue.locaKey}
                                  </div>
                                  <div>{getOverflowDescription(issue)}</div>
                                </div>
                              ))}
                              {issues.length > 2 && (
                                <div className="text-xs text-muted-foreground italic">
                                  +{issues.length - 2} more...
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info when not checked yet */}
      {!report && !isChecking && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          <Info className="mx-auto mb-2" size={24} />
          <p>{t('platform.ui.overflowreportpanel.click_check_all_languages_to_scan_for_overflow_issues')}</p>
          <p className="text-xs mt-1">
            This will test all languages to ensure text fits properly in all elements
          </p>
        </div>
      )}
    </div>
  );
}

